import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  Bot,
  CheckCircle2,
  Clock,
  Headphones,
  Loader2,
  TrendingUp,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { agentsApi, ApiError, type Agent, type AgentRoleFromBackend } from "@/lib/api";

function iconForCategory(category: string): LucideIcon {
  switch (category) {
    case "customer_ops":
      return Headphones;
    case "executive":
      return Users;
    case "engineer":
      return Bot;
    case "analytics":
      return BarChart2;
    case "growth":
      return TrendingUp;
    default:
      return Bot;
  }
}

function unlocksRouteForCode(code: string): string | undefined {
  if (code === "cs.ticket_responder") return "/support";
  return undefined;
}

export function TenantAgents({ tenantCode }: { tenantCode: string }) {
  const { idToken, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [hiringRole, setHiringRole] = useState<string | null>(null);

  const list = useQuery<Agent[], ApiError>({
    queryKey: ["agents", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await agentsApi.list(tenantCode, idToken);
      return res.agents;
    },
    enabled: !!idToken && !!user,
  });

  const roles = useQuery<AgentRoleFromBackend[], ApiError>({
    queryKey: ["agent-roles", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await agentsApi.listRoles(tenantCode, idToken);
      return res.roles;
    },
    enabled: !!idToken && !!user,
  });

  const hireMutation = useMutation<Agent, ApiError, AgentRoleFromBackend>({
    mutationFn: async (role) => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      const res = await agentsApi.hire(tenantCode, role.code, role.display_name, idToken);
      return {
        ...res.agent,
        role_code: res.role_code,
        role_display_name: res.role_display_name,
        role_category: res.role_category,
      } as Agent;
    },
    onMutate: (role) => setHiringRole(role.code),
    onSettled: () => setHiringRole(null),
    onSuccess: (_agent, role) => {
      toast.success(`${role.display_name} hired`);
      queryClient.invalidateQueries({ queryKey: ["agents", tenantCode] });
      const route = unlocksRouteForCode(role.code);
      if (route) {
        navigate(route);
      }
    },
    onError: (err, role) => {
      if (err.code === "already_hired") {
        toast.message(`${role.display_name} is already hired`);
        return;
      }
      console.error("hire", err);
      toast.error("Couldn't hire agent", { description: err.message });
    },
  });

  const hired = list.data ?? [];
  const hiredByCode = new Set(hired.map((a) => a.role_code));

  const isLoading = list.isLoading || roles.isLoading;
  const rolesError = roles.error;

  return (
    <div className="p-8 space-y-8">
      <header>
        <h1 className="font-display text-4xl">Agents</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Hire virtual employees for your workspace. Each one comes with its own portal and API.
        </p>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : rolesError ? (
        <p className="text-sm text-destructive">{rolesError.message}</p>
      ) : (roles.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No agent roles are available for your account.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(roles.data ?? []).map((role) => {
            const isHired = hiredByCode.has(role.code);
            const isHiring = hiringRole === role.code;
            const comingSoon = role.code !== "cs.ticket_responder";
            const unlocksRoute = unlocksRouteForCode(role.code);
            const Icon = iconForCategory(role.category);
            return (
              <Card key={role.code}>
                <CardContent className="p-5 flex flex-col gap-4 h-full">
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{role.display_name}</p>
                        {isHired && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-signal-ok/30 bg-signal-ok/10 px-2 py-0.5 text-[10px] font-medium text-signal-ok">
                            <CheckCircle2 className="h-3 w-3" />
                            Hired
                          </span>
                        )}
                        {comingSoon && !isHired && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            Coming soon
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                        {role.description}
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto">
                    {isHired ? (
                      unlocksRoute ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => navigate(unlocksRoute)}
                        >
                          Open portal
                        </Button>
                      ) : null
                    ) : comingSoon ? (
                      <Button variant="outline" size="sm" className="w-full" disabled>
                        Notify me
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full"
                        disabled={isHiring || hireMutation.isPending}
                        onClick={() => hireMutation.mutate(role)}
                      >
                        {isHiring ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Hire
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
